import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Header from '@/components/feature/Header';
import Footer from '@/components/feature/Footer';
import { articleContent, type ArticleContent } from '@/mocks/article';
import { mainWebsiteUrl } from '@/mocks/categories';

export default function Article() {
  const { t, i18n } = useTranslation();
  const lang = (i18n.language || 'en').split('-')[0];
  const content: ArticleContent = articleContent[lang] || articleContent.en;
  const [openFaq, setOpenFaq] = useState<number | null>(0);

  useEffect(() => {
    document.documentElement.lang = lang;
  }, [lang]);

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: content.faq.map((item) => ({
      '@type': 'Question',
      name: item.q,
      acceptedAnswer: { '@type': 'Answer', text: item.a },
    })),
  };

  const siteUrl = import.meta.env.VITE_SITE_URL;

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: content.title,
    description: content.metaDescription,
    inLanguage: lang,
    author: { '@type': 'Organization', name: 'USFansSheet.casa', url: siteUrl },
    publisher: {
      '@type': 'Organization',
      name: 'USFansSheet.casa',
      logo: {
        '@type': 'ImageObject',
        url: 'https://static.readdy.ai/image/2df560051ef0cafd8dabf8a049058ba4/2277e9ffbdee9fd651886f1aeeba0d56.jpeg',
      },
    },
    datePublished: '2026-08-20',
    dateModified: '2026-08-20',
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': siteUrl ? `${siteUrl}/article/usfans-spreadsheet-guide` : undefined,
    },
  };

  return (
    <div className="min-h-screen bg-background-50">
      <Header />

      <main className="pt-16">
        <div className="mx-auto w-full max-w-6xl px-4 py-10 md:px-6 md:py-14">
          <nav className="mb-8 flex flex-wrap items-center gap-2 text-sm text-foreground-500">
            <Link to="/" className="transition-colors hover:text-primary-600 cursor-pointer">
              {t('nav_home')}
            </Link>
            <span className="w-3 h-3 flex items-center justify-center">
              <i className="ri-arrow-right-s-line" />
            </span>
            <span className="text-foreground-700">{t('nav_guide')}</span>
          </nav>

          <div className="grid grid-cols-1 gap-10 lg:grid-cols-3">
            <article className="lg:col-span-2">
              <span className="inline-flex items-center gap-1.5 rounded-full bg-primary-100 px-3 py-1 text-xs font-semibold text-primary-700">
                <span className="w-3 h-3 flex items-center justify-center">
                  <i className="ri-file-list-3-line" />
                </span>
                {t('read_guide')}
              </span>
              <h1 className="mt-4 font-heading text-2xl font-semibold leading-tight text-foreground-950 md:text-4xl">
                {content.title}
              </h1>
              <p className="mt-5 text-base leading-relaxed text-foreground-700">{content.intro}</p>

              <div className="mt-8 space-y-8">
                {content.sections.map((section) => (
                  <section key={section.heading}>
                    <h2 className="font-heading text-xl font-semibold text-foreground-950 md:text-2xl">
                      {section.heading}
                    </h2>
                    {section.paragraphs.map((p) => (
                      <p key={p.slice(0, 24)} className="mt-3 text-sm leading-relaxed text-foreground-700 md:text-base">
                        {p}
                      </p>
                    ))}
                  </section>
                ))}
              </div>

              <div className="mt-10 rounded-lg border border-background-200/70 bg-white p-6 md:p-8">
                <h2 className="font-heading text-xl font-semibold text-foreground-950 md:text-2xl">
                  {t('faq_title')}
                </h2>
                <div className="mt-5 space-y-3">
                  {content.faq.map((item, idx) => {
                    const isOpen = openFaq === idx;
                    return (
                      <div key={item.q} className="overflow-hidden rounded-lg border border-background-200/60">
                        <button
                          type="button"
                          onClick={() => setOpenFaq(isOpen ? null : idx)}
                          className="flex w-full items-center justify-between gap-4 px-4 py-3 text-left cursor-pointer"
                        >
                          <span className="text-sm font-semibold text-foreground-900">{item.q}</span>
                          <span
                            className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full ${
                              isOpen ? 'bg-primary-100 text-primary-600' : 'bg-background-100 text-foreground-500'
                            }`}
                          >
                            <i className={isOpen ? 'ri-subtract-line' : 'ri-add-line'} />
                          </span>
                        </button>
                        {isOpen && (
                          <div className="px-4 pb-4">
                            <p className="text-sm leading-relaxed text-foreground-600">{item.a}</p>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>

              <p className="mt-8 text-base leading-relaxed text-foreground-700">{content.conclusion}</p>

              <div className="mt-8 flex flex-col gap-3 sm:flex-row">
                <a
                  href={mainWebsiteUrl}
                  target="_blank"
                  rel="noopener nofollow"
                  className="inline-flex items-center justify-center gap-2 rounded-full bg-primary-500 px-6 py-3 text-sm font-semibold text-white transition-colors hover:bg-primary-600 whitespace-nowrap cursor-pointer"
                >
                  {t('visit_main_site')}
                  <span className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-arrow-right-up-line" />
                  </span>
                </a>
                <Link
                  to="/"
                  className="inline-flex items-center justify-center gap-2 rounded-full border border-background-300/70 bg-white px-6 py-3 text-sm font-semibold text-foreground-800 transition-colors hover:bg-background-100 whitespace-nowrap cursor-pointer"
                >
                  <span className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-arrow-left-line" />
                  </span>
                  {t('back_home')}
                </Link>
              </div>
            </article>

            <aside className="lg:col-span-1">
              <div className="sticky top-20 space-y-6">
                <div className="rounded-lg border border-background-200/70 bg-white p-6">
                  <h3 className="font-heading text-base font-semibold text-foreground-950">
                    {t('related_article')}
                  </h3>
                  <p className="mt-3 text-sm leading-relaxed text-foreground-600">
                    {t('article_card_desc')}
                  </p>
                  <Link
                    to="/"
                    className="mt-4 inline-flex items-center gap-2 text-sm font-semibold text-primary-600 cursor-pointer"
                  >
                    {t('nav_home')}
                    <span className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-arrow-right-line" />
                    </span>
                  </Link>
                </div>

                <div className="rounded-lg bg-primary-500 p-6 text-white">
                  <h3 className="font-heading text-base font-semibold">{t('final_cta_buy')}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-white/90">{t('final_desc')}</p>
                  <a
                    href={mainWebsiteUrl}
                    target="_blank"
                    rel="noopener nofollow"
                    className="mt-4 inline-flex items-center gap-2 rounded-full bg-white px-5 py-2.5 text-sm font-semibold text-primary-700 transition-colors hover:bg-background-100 whitespace-nowrap cursor-pointer"
                  >
                    {t('visit_main_site')}
                  </a>
                </div>
              </div>
            </aside>
          </div>
        </div>
      </main>

      <Footer />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }}
      />
    </div>
  );
}